

import { Injectable } from '@angular/core';
import { GeneralCache } from 'src/app/classes/app/general-cache';
import { EOS, EQueues } from 'src/app/classes/def/app/app';
import { TimeUtils } from 'src/app/classes/general/time';
import { GenericQueueService } from '../generic-queue';
import { SettingsManagerService } from '../settings-manager';
import { ResourcesCoreDataService } from '../../data/resources-core';
import { ITutorial } from 'src/app/classes/def/app/tutorials';
import { SleepUtils } from '../../utils/sleep-utils';
import { ResourceManager } from 'src/app/classes/general/resource-manager';
import { BehaviorSubject } from 'rxjs';
import { WaitUtils } from '../../utils/wait-utils';
import { ApiDef } from 'src/app/classes/app/api';
import { Messages } from 'src/app/classes/def/app/messages';
import { IDBModelLanguage } from 'src/app/classes/def/nav-params/story';


var synth = window.speechSynthesis;

interface ITTSPlatformSetup {
    ios: ITTSPlatformOption;
    android: ITTSPlatformOption;
    web: ITTSPlatformOption;
}
interface ITTSPlatformOption {
    enable: boolean;
    app: number;
    content: number;
}

interface ITTSContextOptions {
    app: ITTSContextOption,
    content: ITTSContextOption
}

interface ITTSContextOption {
    browser: SpeechSynthesisVoice,
    cloud: ITTSVoice
}

enum ETTSMode {
    // native = 1,
    browser = 2,
    cloud = 3
}

export interface ITTSVoice {
    name: string;
    uri?: string;
    local?: boolean;
    lang?: string;
    voice?: string;
}

@Injectable({
    providedIn: 'root'
})
export class TextToSpeechService {

    lastTimeSpeechOutput: string = "";
    enabled: boolean = true;
    tutorialsPlayed: number[] = [];


    voiceOptions: ITTSContextOptions = {
        app: {
            browser: null,
            cloud: null
        },
        content: {
            browser: null,
            cloud: null
        }
    };

    observable = {
        locked: null,
        ttsReady: null,
        ttsEvent: null,
        audioEvent: null,
        ttsRunning: null
    };

    subscription = {
        locked: null,
        ttsReady: null
    };

    locked: boolean = false;

    wakeLock: boolean = true;
    ttsReady: boolean = true;

    volumeEnabled: boolean = true;

    platformSetup: ITTSPlatformSetup = {
        ios: {
            enable: true,
            // app: ETTSMode.native, // local voice is good
            app: ETTSMode.cloud,
            content: ETTSMode.cloud
        },
        android: {
            enable: true,
            // app: ETTSMode.native, // local voice is so and so
            app: ETTSMode.cloud,
            content: ETTSMode.cloud
        },
        web: {
            enable: true,
            app: ETTSMode.cloud, // local voice can be terrible
            content: ETTSMode.cloud
        }
    }

    first: boolean = true;
    speechTimeout = null;
    soundPlaying: boolean = false;
    soundPreloaded: boolean = false;

    speechInProgress: boolean = false;

    private audioElement: HTMLAudioElement;
    private lastPlayedText: string = '';

    keepAwakeTimeout: number = 10000;
    keepAwakeTs: number = null;

    constructor(
        public genericQueue: GenericQueueService,
        public resources: ResourcesCoreDataService,
        public settingsProvider: SettingsManagerService
    ) {
        console.log("tts service created");
        this.observable = ResourceManager.initBsubObj(this.observable);
        this.audioElement = new Audio();
        // this.audioElement
        this.initAudioHandlers();
        this.settingsProvider.watchPlatformFlagsLoaded().subscribe((loaded: boolean) => {
            if (loaded) {
                this.initDefaultTTSOptions();

            }
        }, (err: Error) => {
            console.error(err);
        });

        this.settingsProvider.getSettingsLoaded(false).then((res) => {
            if (res) {
                this.volumeEnabled = !SettingsManagerService.settings.app.settings.ttsMute.value;
                console.log("loading settings volume enabled: ", this.volumeEnabled);
            }
        }).catch((err: Error) => {
            console.error(err);
        });
    }

    initAudioHandlers() {
        this.audioElement.addEventListener('play', () => { this.observable.audioEvent.next(true); this.observable.audioEvent.next(null); });
        this.audioElement.addEventListener('ended', () => { this.observable.audioEvent.next(false); this.observable.audioEvent.next(null); });
        this.audioElement.addEventListener('pause', () => { this.observable.audioEvent.next(false); this.observable.audioEvent.next(null); });
    }

    getAudioEventObservable(): BehaviorSubject<boolean> {
        return this.observable.audioEvent;
    }

    getRunningObservable(): BehaviorSubject<boolean> {
        return this.observable.ttsRunning;
    }

    toggleSpeaker(enabled: boolean) {
        this.volumeEnabled = enabled;
        SettingsManagerService.settings.app.settings.ttsMute.value = !enabled;
        this.settingsProvider.updateLocalSettingsNoAction();
        if (!enabled) {
            this.stop(false);
            this.stop(true);
        }
    }

    getSpeakerEnabled() {
        return this.volumeEnabled;
    }

    synthesizeAndPlay(content: string, volume: number, isContent: boolean) {
        return new Promise(async (resolve) => {
            console.log("synthesize tts: ", content);
            let po = this.getPlatformOption();
            let vo = isContent ? this.voiceOptions.content : this.voiceOptions.app;
            await this.speakCloud(content, volume, null, vo.cloud);
            resolve(true);
        });
    }

    playAudio(volume: number) {
        this.audioElement.volume = volume != null ? volume : 1;
        this.audioElement.play().then(() => {
            console.log("audio element play resolved"); // on loaded
        }).catch((err: Error) => {
            console.error(err);
        });
    }

    stopAudio() {
        this.audioElement.pause();
        this.audioElement.currentTime = 0; // Reset audio to the start
        this.observable.audioEvent.next(false); this.observable.audioEvent.next(null);
    }

    /**
     * wait until tts is unlocked (some time after entering bg mode)
     */
    waitUnlocked() {
        let promise = new Promise((resolve) => {
            if (!this.locked) {
                resolve(true);
                return;
            }
            console.log("wait unlocked");
            this.subscription.locked = this.observable.locked.subscribe((locked: boolean) => {
                if (locked === false) {
                    this.subscription.locked = ResourceManager.clearSub(this.subscription.locked);
                    resolve(true);
                }
            });
        });
        return promise;
    }

    /**
     * wait until tts is ready (not playing sound)
     */
    waitTTSReady() {
        let promise = new Promise((resolve) => {
            if (this.ttsReady) {
                resolve(true);
                return;
            }
            this.subscription.ttsReady = this.observable.ttsReady.subscribe((ready: boolean) => {
                if (ready) {
                    this.subscription.ttsReady = ResourceManager.clearSub(this.subscription.ttsReady);
                    resolve(true);
                }
            });
        });
        return promise;
    }

    watchEvent() {
        return this.observable.ttsEvent;
    }


    /**
     * trick to keep the app alive in the background
     * because using audio seems to break the background mode plugin
     * functionality is replaced by this
     */
    keepAwake() {
        if (this.genericQueue.checkSize(EQueues.tts) === 0) {
            if (GeneralCache.os === EOS.ios) {
                // iOS no longer tricked by empty sound, suspends app in background
                // this.textToSpeechQueue(null, true);
                this.textToSpeechQueue("The app is running in background mode", false, 0.01, null, null, false);
                // this.textToSpeechQueue("                                     ", false);
            } else {
                // this.textToSpeechQueue(" ", false, 0.01, null, null, false, true, false);

                // this.textToSpeechQueue("The app is running in background mode", false, 0.01, null, null, false, true, false);
                this.textToSpeechQueue(Messages.tts.bgModeDefault, false, 0.01, null, null, false);

                // this.textToSpeechQueue(" ", false, 1, null, null, false, true, false);
            }
        }
    }

    startStopTTSQueue(text: string, soundHack: boolean, volume: number, onComplete: () => any, timeout: number) {
        if (this.speechInProgress) {
            this.stop(false);
        } else {
            this.textToSpeechQueue(text, soundHack, volume, onComplete, timeout, false);
        }
    }


    /**
     * use cloud TTS
     * https://cloud.google.com/text-to-speech/
     * @param text 
     * @param lang 
     * @param voice 
     * @returns 
     */
    private synthesizeSpeechCloud(text: string, lang: string, voice: string) {
        return new Promise(async (resolve, reject) => {
            const apiKey = ApiDef.ttsApiKey;
            const url = `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`;
            console.log("tts synthesize speech cloud");

            const request = {
                input: { text: text },
                voice: { languageCode: lang != null ? lang : 'en-US', name: voice != null ? voice : 'en-US-Standard-J' },
                audioConfig: {
                    audioEncoding: 'MP3',
                    effectsProfileId: [
                        "handset-class-device"
                    ],
                    pitch: 0,
                    speakingRate: 1
                }
            };

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(request),
                });
                if (!response.ok) {
                    reject(new Error('Network error'));
                    return;
                }
                const data = await response.json();
                resolve(data.audioContent); // This is the base64-encoded audio content
            } catch (err) {
                console.error('Error calling the Text-to-Speech API:', err);
                reject(err);
            }
        });
    }

    /**
     * text to speech
     * add to queue
     * @param text 
     */
    textToSpeechQueue(text: string, soundHack: boolean, volume: number, onComplete: () => any, timeout: number, isContent: boolean) {
        if (!this.checkEnabled() && !soundHack) {
            if (onComplete) {
                onComplete();
            }
            return;
        }

        if (text === null && !soundHack) {
            if (onComplete) {
                onComplete();
            }
            return;
        }

        this.observable.ttsReady.next(false);
        this.ttsReady = false;

        console.log("tts request queue: " + text);

        this.genericQueue.enqueue(() => {

            this.observable.ttsEvent.next(text);
            return this.textToSpeechCoreResolve(text, volume, timeout, isContent);

        }, () => {
            this.ttsReady = true;
            this.observable.ttsReady.next(true);
            this.observable.ttsEvent.next(null);
            if (onComplete) {
                onComplete();
            }
        }, null, EQueues.tts, null, timeout != null ? timeout : 20000);
    }

    async textToSpeechNoAction(text: string, volume: number, onComplete: () => any, timeout: number) {
        if (!this.checkEnabled()) {
            console.warn("tts not enabled");
            return;
        }
        try {
            await this.textToSpeechCoreResolve(text, volume, timeout, false);
            if (onComplete) {
                onComplete();
            }
        } catch (err) {
            console.error(err);
            if (onComplete) {
                onComplete();
            }
        }
    }

    private checkEnabled(): boolean {
        if (!this.enabled) {
            return false;
        }

        if (SettingsManagerService.settings.app.settings.ttsMute.value) {
            return false;
        }

        return this.getPlatformOption().enable;
    }

    getVoicesBrowser(langCode: string): Promise<ITTSVoice[]> {
        return new Promise(async (resolve, reject) => {
            try {
                let voices: SpeechSynthesisVoice[] = synth.getVoices();
                let voicesList: ITTSVoice[] = [];
                for (let voice1 of voices) {
                    if (voice1.lang === langCode) {
                        let v: ITTSVoice = {
                            name: voice1.name,
                            uri: voice1.voiceURI,
                            local: voice1.localService,
                            lang: voice1.lang
                        }
                        voicesList.push(v);

                    }
                }
                resolve(voicesList);
            } catch (err) {
                reject(err);
            }
        });
    }

    getVoicesCloud(langCode: string): Promise<ITTSVoice[]> {
        return new Promise(async (resolve, reject) => {
            const apiKey = ApiDef.ttsApiKey;
            const url = `https://texttospeech.googleapis.com/v1/voices?key=${apiKey}&languageCode=${langCode}`;
            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                if (!response.ok) {
                    reject(new Error('Network error'));
                    return;
                }
                const data = await response.json();
                resolve(data);
            } catch (err) {
                console.error('Error calling the Text-to-Speech API:', err);
                reject(err);
            }
        });
    }



    getPlatformOption(): ITTSPlatformOption {
        switch (GeneralCache.os) {
            case EOS.ios:
                return this.platformSetup.ios;
            case EOS.android:
                return this.platformSetup.android;
            case EOS.browser:
            default:
                return this.platformSetup.web;
        }
    }

    private getTTSVoiceOptionsBrowser(lang: ITTSVoice): SpeechSynthesisVoice {
        if (synth == null) {
            console.warn("synth not available");
            return;
        }
        let voices: SpeechSynthesisVoice[] = synth.getVoices();
        let voice: SpeechSynthesisVoice;
        for (let voice1 of voices) {
            if (voice1.localService && voice1.lang === lang.lang) {
                voice = voice1;
                break;
            }
        }
        return voice;
    }

    private getTTSVoiceOptionsCloud(lang: ITTSVoice) {
        let langOpt: ITTSVoice = {
            name: lang.name,
            lang: lang.name,
            voice: lang.voice
        }
        return langOpt;
    }

    getTTSVoiceOptionsApp(langCode: string) {
        return new Promise(async (resolve) => {
            let po: ITTSPlatformOption = this.getPlatformOption();
            let ttsVoices: ITTSVoice[] = await this.getTTSVoiceOptionsCore(po.app, langCode);
            resolve(ttsVoices);
        });
    }

    getTTSVoiceOptionsContent(langCode: string) {
        return new Promise(async (resolve) => {
            let po: ITTSPlatformOption = this.getPlatformOption();
            let ttsVoices: ITTSVoice[] = await this.getTTSVoiceOptionsCore(po.content, langCode);
            resolve(ttsVoices);
        });
    }

    private getTTSVoiceOptionsCore(context: number, langCode: string) {
        return new Promise<ITTSVoice[]>(async (resolve) => {
            let ttsVoices: ITTSVoice[] = [];
            try {
                switch (context) {                   
                    case ETTSMode.browser:
                        ttsVoices = await this.getVoicesBrowser(langCode);
                        break;
                    case ETTSMode.cloud:
                        ttsVoices = await this.getVoicesCloud(langCode);
                        break;
                }
            } catch (err) {
                console.error(err);
            }
            resolve(ttsVoices);
        });
    }

    /**
     * set TTS voice and language for app messages
     * @param lang 
     */
    setTTSVoiceOptionsApp(lang: ITTSVoice) {
        console.log("set tts voice options / app: ", lang);
        let po: ITTSPlatformOption = this.getPlatformOption();
        console.log("platform option: ", po);
        switch (po.app) {           
            case ETTSMode.browser:
                this.voiceOptions.app.browser = this.getTTSVoiceOptionsBrowser(lang);
                break;
            case ETTSMode.cloud:
                this.voiceOptions.app.cloud = this.getTTSVoiceOptionsCloud(lang);
                break;
        }
        console.log("voice options app set: ", this.voiceOptions.app);
    }

    /**
     * set TTS voice and language for content
     * @param lang 
     */
    setTTSVoiceOptionsContent(langModel: IDBModelLanguage, langOverride: string) {
        console.log("set tts voice options / content: ", langModel);
        let po: ITTSPlatformOption = this.getPlatformOption();
        console.log("platform option: ", po);
        let lang: ITTSVoice = {
            name: langOverride != null ? langOverride : langModel != null ? langModel.langCode : "en-US",
            voice: langModel != null ? langModel.ttsVoice : null
        };
        switch (po.content) {           
            case ETTSMode.browser:
                this.voiceOptions.content.browser = this.getTTSVoiceOptionsBrowser(lang);
                break;
            case ETTSMode.cloud:
                this.voiceOptions.content.cloud = this.getTTSVoiceOptionsCloud(lang);
                break;
        }
        console.log("voice options content set: ", this.voiceOptions.content);
    }

    initDefaultTTSOptions() {
        let defaultVoice: ITTSVoice = {
            name: "en-US",
            lang: "en-US",
            voice: 'en-US-Standard-J'
        };
        // context setup
        this.setTTSVoiceOptionsApp(defaultVoice);
        this.setTTSVoiceOptionsContent(null, defaultVoice.name);
    }

    speakBrowser(text: string, volume: number, timeout: number, opts: SpeechSynthesisVoice) {
        let promise: Promise<boolean> = new Promise(async (resolve) => {
            // if (this.first) {
            //     this.first = false;
            //     console.log("first speech");
            //     let btn = document.getElementById('speechButtonHidden');
            //     console.log(btn);
            //     btn.click();
            // }

            console.log("tts speak browser: ", opts);

            if (synth == null) {
                console.warn("synth not available");
                resolve(false);
                return;
            }

            if (synth.speaking) {
                synth.cancel();
            }

            let btn = document.getElementById('speechButtonHidden');
            console.log(btn);

            btn.onclick = () => {
                console.log("virtual button clicked");
                // https://mdn.github.io/web-speech-api/speak-easy-synthesis/

                let voice: SpeechSynthesisVoice = opts;
                var spk = new SpeechSynthesisUtterance(text);
                spk.onstart = () => {
                    console.log("speech started");
                    this.speechInProgress = true;
                };
                spk.onend = () => {
                    console.log("speech ended");
                    ResourceManager.clearTimeout(this.speechTimeout);
                    this.speechInProgress = false;
                    resolve(true);
                };
                spk.onerror = (event) => {
                    console.error(event);
                    ResourceManager.clearTimeout(this.speechTimeout);
                    this.speechInProgress = false;
                    resolve(false);
                };
                spk.voice = voice;

                spk.pitch = 1;
                spk.rate = 1;
                spk.volume = volume;

                synth.onvoiceschanged = () => {
                    console.log("voice changed");
                };

                synth.speak(spk);

                ResourceManager.clearTimeout(this.speechTimeout);
                this.speechTimeout = setTimeout(() => {
                    console.log("speech timed out");
                    this.speechInProgress = false;
                    resolve(true);
                }, timeout != null ? timeout : 10000);
            };

            btn.click();
        });
        return promise;
    }

    speakCloud(text: string, volume: number, _timeout: number, opts: ITTSVoice) {
        return new Promise(async (resolve) => {
            console.log("tts speak cloud: ", opts);

            if (text === this.lastPlayedText && this.audioElement.src) {
                this.speechInProgress = true;
                this.playAudio(volume);
                await WaitUtils.waitFlagResolve(null, this.observable.audioEvent, [false], 120000);
                this.speechInProgress = false;
                resolve(true);
                return;
            }
            this.lastPlayedText = text;
            const audioContentBase64 = await this.synthesizeSpeechCloud(text, opts.lang, opts.voice);
            if (audioContentBase64) {
                this.audioElement.src = `data:audio/mp3;base64,${audioContentBase64}`;
                this.speechInProgress = true;
                this.playAudio(volume);
            }
            await WaitUtils.waitFlagResolve(null, this.observable.audioEvent, [false], 120000);
            this.speechInProgress = false;
            resolve(true);
        });
    }

    textToSpeechCoreResolve(text: string, volume: number, timeout: number, isContent: boolean): Promise<boolean> {
        let promise: Promise<boolean> = new Promise((resolve) => {
            if (!this.checkEnabled()) {
                console.warn("tts not enabled");
                resolve(false);
                return;
            }

            console.log("tts request core: " + text);

            if (volume == null) {
                volume = 0.7;
            }

            if (!this.volumeEnabled) {
                volume = 0;
            }

            /**
             * ["
             * ku,pt_BR,ta,ja_JP,sk_SK,tr_TR,ru_RU,ko_KR,hi_IN,fil_PH,
             * fi_FI,ca,hr,es_ES,sk,ro_RO,zh_TW,sw,el_GR,fr_BE,en_GB,
             * nl_NL,la,et_EE,pt_PT,fr_FR,bs,km_KH,vi_VN,en_AU,pl_PL,
             * da_DK,sq,cy,en_US,bn_IN,si_LK,in_ID,yue_HK,uk_UA,bn_BD,
             * sr,en_IN,cs_CZ,it_IT,ne_NP,de_DE,es_US,zh_CN,th_TH,sv_SE,nb_NO,hu_HU" ]
             */

            this.waitUnlocked().then(async () => {
                let po = this.getPlatformOption();
                console.log("speak / platform option: ", po);
                let co = isContent ? po.content : po.app;
                let vo = isContent ? this.voiceOptions.content : this.voiceOptions.app;
                console.log("voice option: ", vo);
                // if (volume < 0.1) {
                //     co = GeneralCache.os !== EOS.browser ? ETTSMode.native : ETTSMode.browser; // filler sound hack
                // }
                console.log("tts start context: " + co);
                this.observable.ttsRunning.next(true);
                this.observable.ttsRunning.next(null);
                if (volume === 0) {
                    this.speechInProgress = true;
                    await SleepUtils.sleep(3000);
                    this.speechInProgress = false;
                } else {
                    switch (co) {                        
                        case ETTSMode.browser:
                            await this.speakBrowser(text, volume, timeout, vo.browser);
                            break;
                        case ETTSMode.cloud:
                            await this.speakCloud(text, volume, timeout, vo.cloud);
                            break;
                    }
                }
                this.observable.ttsRunning.next(false);
                this.observable.ttsRunning.next(null);
                resolve(true);
            });
        });
        return promise;
    }

    stopContext(context: number) {
        console.log("tts stop context: " + context);
        switch (context) {            
            case ETTSMode.browser:
                synth.cancel();
                break;
            case ETTSMode.cloud:
                this.stopAudio();
                break;
        }
    }

    stop(cloud: boolean) {
        if (!this.checkEnabled()) {
            console.warn("tts not enabled");
            return;
        }
        let po = this.getPlatformOption();
        this.stopContext(po.content);
        this.stopContext(po.app);
        if (cloud) {
            this.stopAudio();
        }
    }


    /**
     * speak time left if time left matches increment
     * e.g. each 30 seconds
     * adaptive speech (don't include full detail)
     * @param timeLeft 
     * @param increment 
     * @param keepTrackOfChanges trigger speech only if time speech is different
     */
    speakTimeLeft(timeLeft: number, keepTrackOfChanges: boolean, useQueue: boolean) {
        if (!this.checkEnabled()) {
            console.warn("tts not enabled");
            return;
        }
        let message: string = this.getTimeLeftMessageWithContext(timeLeft, keepTrackOfChanges, useQueue);
        if (message) {
            this.textToSpeechWrapper(message, useQueue, null, null, null);
        }
    }

    /**
     * get time left message
     * returns null if time should not be spoken yet
     * @param timeLeft 
     * @param totalTime
     * @param keepTrackOfChanges 
     * @param useQueue 
     */
    getTimeLeftMessageWithContext(timeLeft: number, keepTrackOfChanges: boolean, useQueue: boolean): string {
        let message: string = "";
        const fiveMinutes = 5 * 60;
        const twoMinutes = 2 * 60;
        const oneMinute = 1 * 60;

        if (timeLeft > 0) {
            if (timeLeft >= fiveMinutes) {
                // Speak every 5 minutes
                if (timeLeft % fiveMinutes == 0) {
                    let ttsdata: string[] = TimeUtils.formatTimeSpeechFromSeconds(timeLeft, true);
                    console.log(ttsdata[0]);
                    if (keepTrackOfChanges) {
                        if (ttsdata[0] !== this.lastTimeSpeechOutput) {
                            this.lastTimeSpeechOutput = ttsdata[0];
                            message = ttsdata[0] + " left", useQueue;
                        }
                    } else {
                        message = ttsdata[0] + " left", useQueue;
                    }
                } else {
                    message = null;
                }
            } else {
                // Speak at 2 minutes and 1 minute left
                if (timeLeft == twoMinutes || timeLeft == oneMinute) {
                    let ttsdata: string[] = TimeUtils.formatTimeSpeechFromSeconds(timeLeft, true);
                    console.log(ttsdata[0]);
                    if (keepTrackOfChanges) {
                        if (ttsdata[0] !== this.lastTimeSpeechOutput) {
                            this.lastTimeSpeechOutput = ttsdata[0];
                            message = ttsdata[0] + " left", useQueue;
                        }
                    } else {
                        message = ttsdata[0] + " left", useQueue;
                    }
                } else {
                    message = null;
                }
            }
        } else {
            message = null;
        }
        return message;
    }


    private textToSpeechWrapper(text: string, useQueue: boolean, volume: number, onComplete: () => any, timeout: number) {
        if (!this.checkEnabled()) {
            console.warn("tts not enabled");
            return;
        }
        if (!useQueue) {
            this.textToSpeechNoAction(text, volume, onComplete, timeout);
        } else {
            this.textToSpeechQueue(text, false, volume, onComplete, timeout, false);
        }
    }

    checkAlreadyPlayed(code: number): boolean {
        if (this.tutorialsPlayed.indexOf(code) !== -1) {
            return true;
        }
        return false;
    }

    /**
     * speak using loader code (tutorial from DB)
     * option to read the tutorial only once (for each tutorial code)
     * @param code 
     * @param text if specified, loader code is only virtual, and content is overridden
     * @param useQueue 
     * @param onlyOnce 
     */
    speakUsingLoaderCode(code: number, text: string, useQueue: boolean, onlyOnce: boolean, volume: number, onComplete: () => any, timeout: number) {
        if (!this.checkEnabled()) {
            console.warn("tts not enabled");
            return;
        }
        if (onlyOnce) {
            if (this.checkAlreadyPlayed(code)) {
                return;
            }
        }
        let promiseText: Promise<string> = new Promise((resolve) => {
            if (text != null) {
                resolve(text);
            } else {
                this.resources.getInfoData(code, true).then((resp: ITutorial) => {
                    if (resp && resp.description) {
                        resolve(resp.description);
                    } else {
                        resolve(null);
                    }
                }).catch((err: Error) => {
                    console.error(err);
                    resolve(null);
                });
            }
        });
        promiseText.then((text: string) => {
            if (text != null) {
                this.tutorialsPlayed.push(code);
                this.textToSpeechWrapper(text, useQueue, volume, onComplete, timeout);
            }
        });
    }
}
